import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  CheckIn,
  CheckOutDatetime,
  Client,
  Location,
  ModelFile,
  Notification,
  PaginatedticketResultsSideloadList,
  Services,
  Status,
  Ticket,
  TicketItem,
  TicketRetrieve,
  TicketStandbyPeriod,
  TimeTracking,
  Turbine,
  UserLimitedList,
  WorkLog,
} from "../../app/generated-axios-client";

export type TicketDateEditPayload = {
  id: string;
  field: "start" | "deadline";
  value: string | null;
};

export type SideloadedTicketInfo = {
  tickets: Record<string, Ticket>;
  turbine: Record<string, Turbine>;
  client: Record<string, Client>;
  assignees: Record<string, UserLimitedList>;
  created_by: Record<string, UserLimitedList>;
  location: Record<string, Location>;
};

export type CheckInOutStatus = "idle" | "loading" | "succeeded" | "failed";

export type CheckInGuardState =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "allowed" }
  | {
      status: "blocked";
      // Null when the blocking session is task-level (no work order to link to).
      blockingTicketId: string | null;
      blockingTicketNumber: number | null;
    };

export type WorklogEntityMaps = {
  location: Record<string, string>;
  turbine: Record<string, string>;
  client: Record<string, string>;
  project: Record<string, string>;
};

export interface TicketState {
  ticket: TicketRetrieve | null;
  ticketFiles: ModelFile[] | null;
  timeSessions: TimeTracking[] | null;
  worklogEntries: WorkLog[] | null;
  worklogEntityMaps: WorklogEntityMaps | null;
  ticketDelays: Notification[] | null;
  ticketRejections: Notification[] | null;
  standbyPeriods: TicketStandbyPeriod[] | null;
  list: PaginatedticketResultsSideloadList;
  services: Array<Services> | null;
  servicesObj: Record<string, Services> | null;
  statuses: Array<Status> | null;
  statusesObj: Record<string, Status> | null;
  sideload: SideloadedTicketInfo | null;
  initialValue: Partial<Ticket> | null;
  checkInStatus: CheckInOutStatus;
  checkOutStatus: CheckInOutStatus;
  editTimeEntryStatus: CheckInOutStatus;
  editStandbyPeriodStatus: CheckInOutStatus;
  checkInGuard: CheckInGuardState;
  lastCompletedSessionEndedAt: string | null;
}

const initialState: TicketState = {
  ticket: null,
  ticketFiles: null,
  timeSessions: null,
  worklogEntries: null,
  worklogEntityMaps: null,
  ticketDelays: null,
  ticketRejections: null,
  standbyPeriods: null,
  list: {
    "pagination": {
      "total_items": 0,
      "total_pages": 0,
      "current_page": 0,
      "per_page": 0,
    },
    "results": {
      "tickets": [],
      "turbine": [],
      "client": [],
      "project": [],
      "service": [],
      "coordinator": [],
      "assignees": [],
      "created_by": [],
      "location": [],
      "coordinators": [],
      "managers": [],
    },
  },
  services: null,
  servicesObj: null,
  statuses: null,
  statusesObj: null,
  sideload: null,
  initialValue: null,
  checkInStatus: "idle",
  checkOutStatus: "idle",
  editTimeEntryStatus: "idle",
  editStandbyPeriodStatus: "idle",
  checkInGuard: { status: "idle" },
  lastCompletedSessionEndedAt: null,
};

export const ticketSlice = createSlice({
  name: "ticket",
  initialState,
  reducers: {
    addOrUpdateTicket: (_state, _action: PayloadAction<Ticket>) => {},
    clearTickets: () => {
      return initialState;
    },
    storeTicket: (state, action: PayloadAction<TicketRetrieve>) => {
      state.ticket = action.payload;
    },
    removeTicket: (state) => {
      state.ticket = null;
      state.ticketFiles = null;
      state.timeSessions = null;
      state.worklogEntries = null;
      state.worklogEntityMaps = null;
      state.ticketDelays = null;
      state.ticketRejections = null;
      state.standbyPeriods = null;
      state.checkInStatus = "idle";
      state.checkOutStatus = "idle";
      state.editTimeEntryStatus = "idle";
      state.editStandbyPeriodStatus = "idle";
      state.checkInGuard = { status: "idle" };
      state.lastCompletedSessionEndedAt = null;
    },
    storeTicketFiles: (state, action: PayloadAction<ModelFile[]>) => {
      state.ticketFiles = action.payload;
    },
    storeTimeSessions: (state, action: PayloadAction<TimeTracking[]>) => {
      state.timeSessions = action.payload;
    },
    storeWorklogEntries: (state, action: PayloadAction<WorkLog[]>) => {
      state.worklogEntries = action.payload;
    },
    storeWorklogEntityMaps: (state, action: PayloadAction<WorklogEntityMaps>) => {
      state.worklogEntityMaps = action.payload;
    },
    storeTicketDelays: (state, action: PayloadAction<Notification[]>) => {
      state.ticketDelays = action.payload;
    },
    storeTicketRejections: (state, action: PayloadAction<Notification[]>) => {
      state.ticketRejections = action.payload;
    },
    storeStandbyPeriods: (state, action: PayloadAction<TicketStandbyPeriod[]>) => {
      state.standbyPeriods = action.payload;
    },
    refreshCurrentTicket: (_state, _action: PayloadAction<string>) => {},
    // Re-reads the three sources WO History is assembled from. refreshCurrentTicket
    // covers the ticket and its time sessions only; the worklog and standby period
    // lists are otherwise fetched just once, on navigation to the ticket view.
    refreshTicketHistory: (_state, _action: PayloadAction<string>) => {},
    setReportDone: (_state, _action: PayloadAction<{ id: string; value: boolean }>) => {},
    setRating: (
      _state,
      _action: PayloadAction<{ id: string; value: number; message?: string | null }>
    ) => {},
    deleteTicket: (_state, _action: PayloadAction<string>) => {},
    addTicketSimpleList: (
      state,
      action: PayloadAction<PaginatedticketResultsSideloadList>
    ) => {
      state.list = action.payload;
    },
    addTicketList: (state, action: PayloadAction<PaginatedticketResultsSideloadList>) => {
      state.list = action.payload;
    },
    addTicketSideload: (state, action: PayloadAction<SideloadedTicketInfo>) => {
      state.sideload = action.payload;
    },
    addService: (state, action: PayloadAction<Array<Services>>) => {
      state.services = action.payload;
    },
    addStatus: (state, action: PayloadAction<Array<Status>>) => {
      state.statuses = action.payload;
    },
    addServiceObj: (state, action: PayloadAction<Record<string, Services>>) => {
      state.servicesObj = action.payload;
    },
    addStatusObj: (state, action: PayloadAction<Record<string, Status>>) => {
      state.statusesObj = action.payload;
    },
    setInitialValue: (state, action: PayloadAction<Partial<Ticket>>) => {
      state.initialValue = action.payload;
    },
    saveTicketItems: (
      _state,
      _action: PayloadAction<{ ticketId: string; items: TicketItem[] }>
    ) => {},
    startWorkOrder: (_state, _action: PayloadAction<string>) => {},
    endWorkOrder: (
      _state,
      _action: PayloadAction<{ id: string; summaryText: string }>
    ) => {},
    delayWorkOrder: (
      _state,
      _action: PayloadAction<{
        id: string;
        message?: string;
        standby_to?: string | null;
        standby_from?: string | null;
      }>
    ) => {},
    reviewWorkOrder: (_state, _action: PayloadAction<string>) => {},
    confirmWorkOrder: (_state, _action: PayloadAction<string>) => {},
    rejectWorkOrder: (
      _state,
      _action: PayloadAction<{ id: string; message?: string }>
    ) => {},
    checkIn: (state, _action: PayloadAction<{ ticketId: string; data: CheckIn }>) => {
      state.checkInStatus = "loading";
    },
    checkOut: (
      state,
      _action: PayloadAction<{ ticketId: string; data: CheckOutDatetime }>
    ) => {
      state.checkOutStatus = "loading";
    },
    setCheckInStatus: (state, action: PayloadAction<CheckInOutStatus>) => {
      state.checkInStatus = action.payload;
    },
    verifyCheckInAvailability: (state, _action: PayloadAction<string>) => {
      state.checkInGuard = { status: "checking" };
    },
    setCheckInGuardAllowed: (state) => {
      state.checkInGuard = { status: "allowed" };
    },
    setCheckInGuardBlocked: (
      state,
      action: PayloadAction<{
        blockingTicketId: string | null;
        blockingTicketNumber: number | null;
      }>
    ) => {
      state.checkInGuard = { status: "blocked", ...action.payload };
    },
    resetCheckInGuard: (state) => {
      state.checkInGuard = { status: "idle" };
    },
    // Kept outside checkInGuard because the guard is reset the moment the check-in
    // drawer opens, while the drawer's time picker needs this bound for as long as
    // it stays open.
    setLastCompletedSessionEndedAt: (state, action: PayloadAction<string | null>) => {
      state.lastCompletedSessionEndedAt = action.payload;
    },
    setCheckOutStatus: (state, action: PayloadAction<CheckInOutStatus>) => {
      state.checkOutStatus = action.payload;
    },
    // Addressed to one row, applied by the API to every row sharing its session_id.
    editTimeEntry: (
      state,
      _action: PayloadAction<{
        entryId: string;
        ticketId: string;
        started_at?: string;
        ended_at?: string;
      }>
    ) => {
      state.editTimeEntryStatus = "loading";
    },
    setEditTimeEntryStatus: (state, action: PayloadAction<CheckInOutStatus>) => {
      state.editTimeEntryStatus = action.payload;
    },
    // Corrects a recorded standby period. Only started_at, ended_at and message are
    // accepted by the API; every other field of the period is server-managed.
    editStandbyPeriod: (
      state,
      _action: PayloadAction<{
        periodId: string;
        ticketId: string;
        started_at: string;
        ended_at?: string;
        message?: string;
      }>
    ) => {
      state.editStandbyPeriodStatus = "loading";
    },
    setEditStandbyPeriodStatus: (state, action: PayloadAction<CheckInOutStatus>) => {
      state.editStandbyPeriodStatus = action.payload;
    },
    ticketDateEdit: (_state, _action: PayloadAction<TicketDateEditPayload>) => {},
    updateTicketDateInList: (
      state,
      action: PayloadAction<{
        id: string;
        field: "start" | "deadline";
        value: string | null;
      }>
    ) => {
      const ticket = state.list.results.tickets.find((t) => t.id === action.payload.id);
      if (ticket) {
        ticket[action.payload.field] = action.payload.value;
      }
    },
  },
});

export const ticketActions = ticketSlice.actions;
export default ticketSlice.reducer;
